import { ChevronRightIcon, PlusCircleIcon } from "@heroicons/react/24/outline";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  fetchTagDefinitions,
  createTagDefinition,
  updateTagDefinition,
  deleteTagDefinition,
} from "../../../api/tagDefinitions";
import { ResourceLoading } from "../../utils/ResourceLoading";
import { ResourceEmpty } from "../../utils/ResourceEmpty";
import { TagDefinitionDialog } from "./TagDefinitionDialog";

export function TagDefinitionsSection() {
  const { t } = useTranslation();

  const scopeModuleLabels = {
    case: t("Cases"),
    project: t("Projects"),
    customer: t("Customers"),
    task: t("Tasks"),
    "": t("All modules"),
  };
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedTag, setSelectedTag] = useState(null);
  const [scopeFilter, setScopeFilter] = useState("");

  const loadTags = () => {
    setLoading(true);
    fetchTagDefinitions(scopeFilter || null)
      .then((data) => setTags(Array.isArray(data) ? data : []))
      .catch(() => setTags([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadTags();
  }, [scopeFilter]);

  const handleItemClick = (tag) => {
    setSelectedTag(tag);
    setModalOpen(true);
  };

  const handleClose = () => {
    setModalOpen(false);
    setSelectedTag(null);
  };

  const handleSaved = () => {
    loadTags();
    handleClose();
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t("Are you sure you want to delete this label?"))) return;
    const res = await deleteTagDefinition(id);
    if (res?.success !== false) {
      loadTags();
      handleClose();
    }
  };

  return (
    <div id="tag-definitions" className="rounded-lg border border-gray-200 bg-white shadow-sm">
      <div className="flex flex-col gap-4 border-b border-gray-200 p-4 sm:flex-row sm:items-center sm:justify-between">
        <h3 className="text-lg font-medium leading-6 text-gray-900">
          {t("Label administration")}
        </h3>
        <div className="flex items-center gap-2">
          <select
            value={scopeFilter}
            onChange={(e) => setScopeFilter(e.target.value)}
            className="rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
          >
            <option value="">{t("All modules")}</option>
            <option value="case">{t("Cases")}</option>
            <option value="project">{t("Projects")}</option>
            <option value="customer">{t("Customers")}</option>
          </select>
          <button
            type="button"
            onClick={() => {
              setSelectedTag(null);
              setModalOpen(true);
            }}
            className="inline-flex items-center gap-1 rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700"
          >
            <PlusCircleIcon className="h-5 w-5" />
            {t("Add label")}
          </button>
        </div>
      </div>
      <div className="max-h-[400px] overflow-auto">
        <ul role="list" className="divide-y divide-gray-200">
          {loading ? (
            <li className="p-8">
              <ResourceLoading />
            </li>
          ) : tags.length === 0 ? (
            <li className="p-8">
              <ResourceEmpty />
            </li>
          ) : (
            tags.map((tag) => (
              <li
                key={tag.id}
                onClick={() => handleItemClick(tag)}
                className="cursor-pointer hover:bg-gray-50"
              >
                <div className="flex items-center px-4 py-4 sm:px-6">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      {tag.color && (
                        <span
                          className="h-4 w-4 rounded-full flex-shrink-0"
                          style={{ backgroundColor: tag.color }}
                        />
                      )}
                      <p className="truncate font-medium text-gray-900">
                        {tag.name}
                      </p>
                      {tag.scope_module && (
                        <span className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                          {scopeModuleLabels[tag.scope_module] ||
                            tag.scope_module}
                        </span>
                      )}
                    </div>
                    {tag.color && (
                      <p className="mt-0.5 text-xs text-gray-500">
                        {tag.color}
                      </p>
                    )}
                  </div>
                  <ChevronRightIcon
                    className="h-5 w-5 flex-shrink-0 text-gray-400"
                    aria-hidden="true"
                  />
                </div>
              </li>
            ))
          )}
        </ul>
      </div>
      <TagDefinitionDialog
        open={modalOpen}
        onClose={handleClose}
        tag={selectedTag}
        onSaved={handleSaved}
        onDelete={handleDelete}
      />
    </div>
  );
}
